import React from 'react';
import { PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';

/**
 * Confirmation dialog for editing fields or deleting items
 */
const ConfirmationDialog = ({ 
  open, 
  onClose, 
  title = "Confirm Field Change", 
  message = null,
  oldValue, 
  newValue, 
  fieldName, 
  onConfirm, 
  onCancel,
  confirmButtonText = "Save Changes",
  confirmButtonClass = "bg-indigo-600 hover:bg-indigo-500",
  icon = "pencil" // 'pencil' or 'warning'
}) => {
  // Determine if we should show comparison (for field edits) or just a message (for deletes)
  const showComparison = oldValue !== undefined && newValue !== undefined;
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95"
          >
            <div>
              <div className={`mx-auto flex size-12 items-center justify-center rounded-full ${icon === 'warning' ? 'bg-red-100' : 'bg-blue-100'}`}>
                {icon === 'warning' ? (
                  <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                ) : (
                  <PencilSquareIcon aria-hidden="true" className="size-6 text-blue-600" />
                )}
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  {message ? (
                    <p className="text-sm text-gray-500">{message}</p>
                  ) : showComparison ? (
                    <p className="text-sm text-gray-500">
                      Are you sure you want to update the {fieldName?.replace('_', ' ')}?
                    </p>
                  ) : null}
                  
                  {showComparison && (
                    <div className="mt-3 rounded-md bg-gray-50 p-3 text-left">
                      <div className="mb-1">
                        <span className="text-xs font-medium text-gray-500">Original:</span>
                        <p className="text-sm font-medium text-red-500 line-through">{oldValue || '(empty)'}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">New:</span>
                        <p className="text-sm font-medium text-green-600">{newValue || '(empty)'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${confirmButtonClass}`}
              >
                {confirmButtonText}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={onCancel}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:col-start-1 sm:mt-0"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmationDialog;